import * as React from "react";
import {
  Select,
  InteractionTag,
  InteractionTagPrimary,
  InteractionTagSecondary,
  TagGroup,
  Button,
  makeStyles,
  useId,
  TagGroupProps,
  Overflow,
  OverflowItem,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  useIsOverflowItemVisible,
  useOverflowMenu,
  shorthands,
  tokens,
  tagClassNames,
} from "@fluentui/react-components";
import { useAtom } from "jotai";
import {
  companyPackagesAtom,
  selectedBenefitsAtom,
  expandedBenefitsAtom,
  filteredCompanyPackagesAtom,
  visibleBenefitTagsAtom,
} from "store/UIHrPage";
import { useTranslation } from "react-i18next";

const useStyles = makeStyles({
  root: {
    display: "flex",
    alignItems: "center",
    gap: "10px", // Space between label, select, and tags
  },
  selectGroup: {
    display: "flex",
    alignItems: "center",
    gap: "10px", // Space between Select and Button
    padding: "5px",
  },
  select: {
    width: "200px", // Set the width of the Select component
  },
  tagGroup: {
    display: "flex",
    flexWrap: "wrap", // Allow tags to wrap to the next line if needed
    gap: "5px", // Space between tags
  },
  container: {
    overflow: "hidden",
    padding: "5px",
    zIndex: 0, // stop the browser resize handle from piercing the overflow menu
    height: "fit-content",
    minWidth: "150px",
    resize: "horizontal",
    width: "100%",
    boxSizing: "border-box",
  },
});
//----- OverflowMenuItem -----//

type OverflowMenuItemProps = {
  tag: {
    children: string;
    value: string;
  };
};

const useMenuItemStyles = makeStyles({
  menuItem: {
    padding: `${tokens.spacingVerticalSNudge} ${tokens.spacingHorizontalXS}`,
    ":hover": {
      [`& .${tagClassNames.root}`]: {
        color: tokens.colorNeutralForeground2Hover,
      },
    },
  },
  tag: {
    backgroundColor: "transparent",
    ...shorthands.borderColor("transparent"),
  },
});

/**
 * A menu item for an overflow menu that only displays when the tag is not visible
 */
const OverflowMenuItem = (props: OverflowMenuItemProps) => {
  const { tag } = props;
  const isVisible = useIsOverflowItemVisible(tag.value);

  const styles = useMenuItemStyles();

  if (isVisible) {
    return null;
  }

  return (
    <MenuItem key={tag.value} className={styles.menuItem}>
      <InteractionTag key={tag.value} value={tag.value} size="small">
        <InteractionTagPrimary hasSecondaryAction>
          {tag.children}
        </InteractionTagPrimary>
        <InteractionTagSecondary aria-label="remove" />
      </InteractionTag>
    </MenuItem>
  );
};

//----- OverflowMenu -----//

/**
 * A menu for viewing tags that have overflowed and are not visible.
 */
const OverflowMenu = ({
  visibleTags,
}: {
  visibleTags: Array<{ children: string; value: string }>;
}) => {
  const { ref, isOverflowing, overflowCount } =
    useOverflowMenu<HTMLButtonElement>();

  if (!isOverflowing) {
    return null;
  }

  return (
    <InteractionTag size="small">
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <InteractionTagPrimary
            ref={ref}
            aria-label={`${overflowCount} more tags`}
          >
            {`+${overflowCount}`}
          </InteractionTagPrimary>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            {visibleTags.map((item) => (
              <OverflowMenuItem key={item.value} tag={item} />
            ))}
          </MenuList>
        </MenuPopover>
      </Menu>
    </InteractionTag>
  );
};

const BenefitTypes = () => {
  const [originalCompanyPackages] = useAtom(companyPackagesAtom);
  const [filteredCompanyPackages, setFilteredCompanyPackages] = useAtom(
    filteredCompanyPackagesAtom
  );

  const [, setSelectedCompanyPackages] = useAtom(selectedBenefitsAtom);
  const [, setExpandedCompanyPackages] = useAtom(expandedBenefitsAtom);

  const { t } = useTranslation();

  const comboId = useId();
  const styles = useStyles();

  // State to track selected option
  const [selected, setSelected] = React.useState<string>("");

  // State to track options available in the dropdown
  const [taggedOptions, setTaggedOptions] = React.useState(() =>
    Array.from(
      new Set(
        (originalCompanyPackages ?? []).map(
          (companyPackage) => companyPackage.benefitCategoryName
        )
      )
    ).map((e) => ({ value: e ?? "", children: e ?? "" }))
  );

  // State to track tags visible in the TagGroup
  const [visibleTags, setVisibleTags] = useAtom(visibleBenefitTagsAtom);

  // Filter employees based on the selected tags
  const filterCompanyPackagesByTags = (tags: Array<{ value: string }>) => {
    if (tags.length === 0) {
      return originalCompanyPackages; // If no tags are selected, show all employees
    }

    return originalCompanyPackages?.filter((companyPackage) =>
      tags.some((item) => companyPackage.benefitCategoryName === item.value)
    );
  };

  React.useEffect(() => {
    const filteredPackages = filterCompanyPackagesByTags(visibleTags);
    setFilteredCompanyPackages(filteredPackages ?? []); // Update the atom with the filtered list
  }, [visibleTags]);

  const addTag = () => {
    const selectedOption = taggedOptions.find(
      (option) => option.value === selected
    );
    if (selectedOption && !visibleTags.includes(selectedOption)) {
      setVisibleTags([...visibleTags, selectedOption]);
      setTaggedOptions(
        taggedOptions.filter((option) => option.value !== selectedOption.value)
      );
      // Clear selected and expanded employees when tags change
      setSelectedCompanyPackages(new Set<string>());
      setExpandedCompanyPackages(new Set<string>());
      setSelected(""); // Reset selected state after adding the tag
    }
  };

  const removeTag: TagGroupProps["onDismiss"] = (_e, { value }) => {
    const removedTag = visibleTags.find((tag) => tag.value === value);
    setVisibleTags(visibleTags.filter((tag) => tag.value !== value));
    if (removedTag) {
      setTaggedOptions([...taggedOptions, removedTag]);
    }
  };

  return (
    <div className={styles.root} data-help="hr-bulk-benefit-type-filter">
      <label id={comboId}>{t("hbh.benefitTypes.label")}</label>
      <div className={styles.selectGroup}>
        <Select
          size="small" // Set the size of the Select component
          id={comboId}
          value={selected}
          onChange={(e) => setSelected(e.target.value)} // Update selected state on change
          className={styles.select}
          title={t("hbh.benefitTypes.selectTags.tooltip")}
        >
          <option value={""}></option>
          {taggedOptions
            .sort((a, b) => a.value.localeCompare(b.value))
            .map((option) => (
              <option key={option.value} value={option.value}>
                {option.children}
              </option>
            ))}
        </Select>
        <Button onClick={addTag} disabled={!selected} size="small">
          {t("hbh.benefitTypes.addButton.label")}
        </Button>
      </div>
      <div className={styles.container}>
        <Overflow minimumVisible={2} padding={60}>
          <TagGroup
            onDismiss={removeTag}
            aria-label={t("hbh.benefitTypes.tagGroup.label")}
            className={styles.tagGroup}
          >
            {visibleTags.map((tag) => (
              <OverflowItem key={tag.value} id={tag.value}>
                <InteractionTag key={tag.value} value={tag.value} size="small">
                  <InteractionTagPrimary hasSecondaryAction>
                    {tag.children}
                  </InteractionTagPrimary>
                  <InteractionTagSecondary
                    aria-label={t("hbh.benefitTypes.tagRemove.tooltip")}
                  />
                </InteractionTag>
              </OverflowItem>
            ))}
            <OverflowMenu visibleTags={visibleTags} />
          </TagGroup>
        </Overflow>
      </div>
    </div>
  );
};

export default BenefitTypes;
